import { FunctionComponent } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { RepairFix, RepairRun } from "../schema/admin-fixes";

const STATUS_STYLES: Record<RepairRun["status"], string> = {
	queued: "bg-gray-100 text-gray-700",
	running: "bg-blue-100 text-blue-800",
	done: "bg-green-100 text-green-800",
	failed: "bg-red-100 text-red-800",
};

/** The one-line outcome of a finished run — the numbers an operator actually checks. */
const summarize = (run: RepairRun, t: TFunction): string => {
	if (run.status === "failed") {
		return run.error ?? t("adminFixes.runs.failed");
	}

	if (!run.result) {
		return t("adminFixes.runs.noReportYet");
	}

	const changed = run.dryRun ? (run.result.planned ?? 0) : (run.result.applied ?? 0);

	return t("adminFixes.runs.summary", {
		changed,
		titles: run.result.titlesResolved ?? 0,
		withoutSeniority: run.result.withoutSeniority ?? 0,
	});
};

export const RunsTable: FunctionComponent<{ runs: RepairRun[]; fixes: RepairFix[] }> = ({
	runs,
	fixes,
}) => {
	const { t } = useTranslation();

	if (runs.length === 0) {
		return <p className="text-xs text-gray-500">{t("adminFixes.runs.empty")}</p>;
	}

	return (
		<div className="overflow-x-auto">
			<table className="w-full min-w-[720px] text-left text-xs">
				<thead className="text-gray-600">
					<tr>
						<th className="py-2 pr-4 font-medium">{t("adminFixes.runs.fix")}</th>
						<th className="py-2 pr-4 font-medium">{t("adminFixes.runs.list")}</th>
						<th className="py-2 pr-4 font-medium">{t("adminFixes.runs.status")}</th>
						<th className="py-2 pr-4 font-medium">{t("adminFixes.runs.outcome")}</th>
						<th className="py-2 font-medium">{t("adminFixes.runs.started")}</th>
					</tr>
				</thead>
				<tbody>
					{runs.map((run) => (
						<tr key={run.id} className="border-t border-gray-100 align-top">
							<td className="py-2 pr-4">
								{fixes.find((fix) => fix.code === run.fixCode)?.name ?? run.fixCode}
								{run.dryRun && (
									<span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
										{t("adminFixes.dryRun")}
									</span>
								)}
							</td>
							<td className="py-2 pr-4">
								{run.listId ? (
									<>
										#{run.listId}
										<span className="ml-1 text-gray-500">
											{t("adminFixes.runs.company", { id: run.companyId })}
										</span>
									</>
								) : (
									// An instance-wide run names no list; its companyId records who asked
									// for it, not what it touched, so showing it here would mislead.
									<span className="text-gray-500">{t("adminFixes.runs.instanceWide")}</span>
								)}
							</td>
							<td className="py-2 pr-4">
								<span className={`rounded px-1.5 py-0.5 ${STATUS_STYLES[run.status]}`}>
									{t(`adminFixes.status.${run.status}`)}
								</span>
							</td>
							<td className="max-w-[320px] py-2 pr-4 text-gray-700">{summarize(run, t)}</td>
							<td className="py-2 text-gray-500">{run.createdAt.toLocaleString()}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};
